import { message } from 'antd';
import { AnyObject, RecordType, ClientSideException } from '@xbcb/shared-types';
import { client, fetchLatestRecord } from '@xbcb/apollo-client';
import { WorkOrderTaskStatus } from '@xbcb/work-order-types';
import { reportError, recordUIEvent, getEnv } from '@xbcb/ui-utils';
import { getRecordFromResponseV2 } from '@xbcb/shared-queries';
import { assignmentRequest, assignWorkOrderTask } from './queries';
import {
  isDeletedWorkOrder,
  isNotAssignedWorkOrderTask,
  constructRedirectToPath,
} from './util';
import type { WorkOrderTask } from '@xbcb/api-gateway-client';
import type { GraphQLError } from 'graphql';
import { v4 as uuidV4 } from 'uuid';
import { setActiveBroker } from '../../actions';
import { store } from 'store';
import { RumUIEvent, UiStage } from '@xbcb/ui-types';
import {
  getAssignmentRequestCooldownRemainingMs,
  runAssignmentRequestExclusively,
  startAssignmentRequestCooldown,
} from './throttle';
const loadingMessage = 'Get new assignment request sent';
const primaryTeamTaskMessage = 'Task assigned from your primary team queue';
const supportTeamTaskMessage = 'Task assigned from your support team queue';
const errorMessage = 'No new tasks found, try again later';
const errorMessageDurationSeconds = 5.0;
const inFlightMessage = 'A new assignment request is already in progress';
const getCooldownMessage = (seconds: number) =>
  `No new tasks found, try again in ${seconds}s`;
const throttleMessageKey = 'assignment-request-throttled';
interface newAssignmentResponse {
  success: boolean;
  workOrderTasks?: WorkOrderTask[];
  isSupportTeamAssignment?: boolean;
}

export type AssignmentRequestOutcome =
  | 'assigned'
  | 'empty'
  | 'error'
  | 'throttled';

export interface ProcessNewAssignmentResponse {
  outcome: AssignmentRequestOutcome;
  redirectTo?: string;
}

const THROTTLED: ProcessNewAssignmentResponse = { outcome: 'throttled' };

const { stage } = getEnv();

const showCooldownMessage = (remainingMs: number): void => {
  void message.warning({
    content: getCooldownMessage(Math.ceil(remainingMs / 1000)),
    key: throttleMessageKey,
  });
};

const unlessInCooldown = async (
  request: () => Promise<ProcessNewAssignmentResponse>,
): Promise<ProcessNewAssignmentResponse> => {
  const remainingMs = getAssignmentRequestCooldownRemainingMs();
  if (remainingMs > 0) {
    showCooldownMessage(remainingMs);
    return THROTTLED;
  }
  return request();
};

export const processNewAssignmentRequest = async (
  user: AnyObject,
): Promise<ProcessNewAssignmentResponse> =>
  unlessInCooldown(async () => {
    const outcome = await runAssignmentRequestExclusively(() =>
      unlessInCooldown(() => requestAndAssignNewTask(user)),
    );
    if (!outcome.ran) {
      void message.info({ content: inFlightMessage, key: throttleMessageKey });
      return THROTTLED;
    }
    return outcome.result;
  });

const backOffWithNoTaskError = (
  outcome: Extract<AssignmentRequestOutcome, 'empty' | 'error'>,
): ProcessNewAssignmentResponse => {
  startAssignmentRequestCooldown();
  void message.error(errorMessage, errorMessageDurationSeconds);
  return { outcome };
};

type TaskAssignmentAttempt =
  | { kind: 'assigned'; redirectTo: string }
  | { kind: 'skipped' }
  | { kind: 'unexpectedError' };

const showFailedToAssignTaskMessage = (workOrderTask: WorkOrderTask): void => {
  void message.error(
    `Failed to assign work order task ${workOrderTask?.id}, checking next task.`,
  );
};

const handleAssignMutationErrors = (
  workOrderTask: WorkOrderTask,
  errors: readonly GraphQLError[],
): TaskAssignmentAttempt => {
  showFailedToAssignTaskMessage(workOrderTask);

  /**
   * Version mismatches are currently expected behavior, hence {@link getNewAssignmentRequest} returning up to 5 WOTs.
   */
  const unexpectedErrors = errors.filter(
    (error) => error.extensions.code !== ClientSideException.VERSION_MISMATCH,
  );
  unexpectedErrors.forEach((error) => {
    reportError(error);
  });
  if (errors.length !== unexpectedErrors.length) {
    // Had a version mismatch error
    recordUIEvent(RumUIEvent.OUTDATED_ASSIGNMENT_TASK, {
      workOrderTaskId: workOrderTask.id,
      message: 'AssignWorkOrderTaskMutationException',
    });
  }
  return unexpectedErrors.length > 0
    ? { kind: 'unexpectedError' }
    : { kind: 'skipped' };
};

const fetchLatestWorkOrderTask = (workOrderTask: WorkOrderTask) =>
  fetchLatestRecord({
    id: workOrderTask.id,
    recordType: RecordType.WORK_ORDER_TASK,
    fields: `id version status definition { workOrderTaskType } workOrder { id deleted {time} group { customsBroker {id} }}`,
  });

type LatestWorkOrderTask = Awaited<ReturnType<typeof fetchLatestWorkOrderTask>>;

const isTaskStillAssignable = (latestRecord: LatestWorkOrderTask): boolean =>
  !isDeletedWorkOrder(latestRecord) && isNotAssignedWorkOrderTask(latestRecord);

const handleStaleTask = (
  workOrderTask: WorkOrderTask,
): TaskAssignmentAttempt => {
  showFailedToAssignTaskMessage(workOrderTask);
  // We want to track how often these occur even if they are expected
  recordUIEvent(RumUIEvent.OUTDATED_ASSIGNMENT_TASK, {
    workOrderTaskId: workOrderTask.id,
    message: 'ShouldSkipMutationException',
  });
  return { kind: 'skipped' };
};

const assignTaskToUser = (latestRecord: LatestWorkOrderTask, user: AnyObject) =>
  client.mutate({
    mutation: assignWorkOrderTask,
    variables: {
      id: latestRecord.id,
      version: latestRecord.version,
      input: {
        status: WorkOrderTaskStatus.ASSIGNED,
        assignee: {
          id: user.id,
        },
      },
      idempotencyKey: uuidV4(),
    },
    errorPolicy: 'all',
  });

type AssignTaskResult = Awaited<ReturnType<typeof assignTaskToUser>>;

const onTaskAssigned = (
  data: AssignTaskResult['data'],
  latestRecord: LatestWorkOrderTask,
  successMessage: string,
): TaskAssignmentAttempt => {
  const redirectTo = constructRedirectToPath(
    getRecordFromResponseV2({
      response: { data },
      crudOrSearchType: 'update',
      recordName: RecordType.WORK_ORDER_TASK,
    }),
  );
  void message.success(successMessage);
  if (stage !== UiStage.PROD) {
    const brokerId = latestRecord.workOrder.group.customsBroker.id;
    store.dispatch(setActiveBroker(brokerId));
  }
  return { kind: 'assigned', redirectTo };
};

const tryAssignTask = async (
  workOrderTask: WorkOrderTask,
  user: AnyObject,
  successMessage: string,
): Promise<TaskAssignmentAttempt> => {
  try {
    const latestRecord = await fetchLatestWorkOrderTask(workOrderTask);
    if (!isTaskStillAssignable(latestRecord)) {
      return handleStaleTask(workOrderTask);
    }
    const { errors, data } = await assignTaskToUser(latestRecord, user);
    if (errors) {
      return handleAssignMutationErrors(workOrderTask, errors);
    }
    return onTaskAssigned(data, latestRecord, successMessage);
  } catch (error) {
    reportError(error);
    return { kind: 'unexpectedError' };
  }
};

const requestAndAssignNewTask = async (
  user: AnyObject,
): Promise<ProcessNewAssignmentResponse> => {
  void message.loading(loadingMessage);
  const { success, workOrderTasks, isSupportTeamAssignment } =
    await getNewAssignmentRequest(user);

  if (!success) {
    return backOffWithNoTaskError('error');
  }
  if (!workOrderTasks?.length) {
    return backOffWithNoTaskError('empty');
  }
  const successMessage = isSupportTeamAssignment
    ? supportTeamTaskMessage
    : primaryTeamTaskMessage;

  let hadUnexpectedError = false;
  for (const workOrderTask of workOrderTasks) {
    const attempt = await tryAssignTask(workOrderTask, user, successMessage);
    if (attempt.kind === 'assigned') {
      return { outcome: 'assigned', redirectTo: attempt.redirectTo };
    }
    if (attempt.kind === 'unexpectedError') {
      hadUnexpectedError = true;
    }
  }
  return backOffWithNoTaskError(hadUnexpectedError ? 'error' : 'empty');
};

const getNewAssignmentRequest = async (
  user: AnyObject,
): Promise<newAssignmentResponse> => {
  try {
    const {
      data: {
        WorkOrderTaskAssignmentRequest: {
          workOrderTasks,
          isSupportTeamAssignment,
        },
      },
    } = await client.query({
      query: assignmentRequest,
      variables: {
        operatorUserId: user.id,
        // Adding version 0 for back compatibility.
        // Todo: Remove the param after version dependency is removed from WorkOrderTaskAssignmentRequest api input schema
        operatorUserVersion: 0,
      },
      fetchPolicy: 'network-only',
    });

    return {
      success: true,
      workOrderTasks,
      isSupportTeamAssignment,
    };
  } catch (e) {
    reportError(e);
  }
  return {
    success: false,
  };
};
